<template>
  <div class="chat-modal" dir="rtl">
    <!-- Header strip: who this conversation belongs to. The candidate is
         identified once, at the top, so the chat surface below stays pure
         conversation. -->
    <div class="chat-head">
      <div class="chat-avatar">{{ initials }}</div>
      <div class="chat-ident">
        <div class="chat-name">{{ displayName }}</div>
        <div class="chat-sub">
          <span v-if="phone" class="chat-phone" dir="ltr">{{ phone }}</span>
          <span v-if="phone && positionTitle" class="chat-sep">·</span>
          <span v-if="positionTitle" class="chat-position">{{ positionTitle }}</span>
        </div>
        <div v-if="statsLine" class="chat-stats">{{ statsLine }}</div>
      </div>
    </div>

    <!-- Loading: three skeleton bubbles rather than a spinner, so the shape of
         what is coming is already on screen. -->
    <div v-if="loading" class="chat-surface" ref="surface">
      <div v-for="n in 3" :key="'skeleton-' + n" class="skeleton-row" :class="n === 2 ? 'is-out' : 'is-in'">
        <div class="skeleton-bubble">
          <div class="skeleton-line"></div>
          <div class="skeleton-line is-short"></div>
        </div>
      </div>
    </div>

    <div v-else-if="error" class="chat-state chat-state-error">
      <p class="chat-state-text">{{ error }}</p>
      <button type="button" class="chat-retry" @click="load">נסה שוב</button>
    </div>

    <div v-else-if="!timeline.length" class="chat-state">
      <p class="chat-state-text">לא נמצאה שיחה של המועמד עם הצ'אטבוט</p>
    </div>

    <div v-else class="chat-surface" ref="surface">
      <template v-for="(item, index) in timeline">
        <div v-if="item.kind === 'day'" :key="'day-' + index" class="chat-pill-row">
          <span class="chat-pill">{{ item.label }}</span>
        </div>
        <div v-else-if="item.kind === 'boundary'" :key="'boundary-' + index" class="chat-pill-row">
          <span class="chat-pill chat-pill-boundary">{{ item.label }}</span>
        </div>
        <div
          v-else
          :key="'msg-' + index"
          class="bubble-row"
          :class="item.message.direction === 'in' ? 'is-in' : 'is-out'"
        >
          <div class="bubble" :class="item.message.direction === 'in' ? 'is-in' : 'is-out'">
            <div v-if="mediaLabel(item.message)" class="bubble-media">{{ mediaLabel(item.message) }}</div>
            <div class="bubble-text">{{ item.message.content }}</div>
            <div class="bubble-meta">
              <span class="bubble-time">{{ timeOf(item.message) }}</span>
              <span v-if="item.message.classification" class="bubble-chip">{{ item.message.classification }}</span>
              <span v-if="item.message.is_template" class="bubble-chip">תבנית</span>
              <span v-if="isFailed(item.message)" class="bubble-failed">לא נשלחה</span>
            </div>
          </div>
        </div>
      </template>
    </div>

    <div class="chat-foot">
      <a
        v-if="applicantId"
        class="chat-panel-link"
        :href="chatbotPanelUrl"
        target="_blank"
        rel="noopener noreferrer"
      >פתיחה בפאנל הצ'אטבוט</a>
      <span v-else></span>
      <button type="button" class="chat-refresh" :disabled="loading" @click="load">רענון</button>
    </div>
  </div>
</template>

<script>
// The candidate's full chatbot history, opened from a table row (the BA
// drilldown on the pipeline-funnel page).
//
// IMPORTANT about the data: conv-svc's /conversations/:id/messages returns
// EVERY message of that applicant, across ALL of their conversations, each row
// tagged with its own conversation_id + conversation_started_at. So any one of
// the applicant's conversation ids is enough to render the whole history — this
// component never has to page through conversations. What it does instead is
// draw a "שיחה חדשה" separator wherever conversation_id changes, so a reader can
// still see where one conversation ended and the next began.
export default {
  name: 'ChatbotConversationModal',
  props: {
    payload: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      loading: false,
      error: '',
      messages: []
    }
  },
  computed: {
    resourceData () {
      return this.payload.resource_data || {}
    },
    applicant () {
      return this.resourceData.applicant || {}
    },
    applicantId () {
      return this.applicant.resource_id || null
    },
    conversationId () {
      return this.resourceData.conversation_id || null
    },
    positionTitle () {
      return this.resourceData.position_title || ''
    },
    displayName () {
      const joined = [this.applicant.first_name, this.applicant.last_name]
        .filter(Boolean)
        .join(' ')
        .trim()
      return this.applicant.full_name || joined || 'מועמד'
    },
    phone () {
      return this.applicant.phone_number || ''
    },
    initials () {
      const source = String(this.displayName || '').trim()
      if (!source) return '—'
      const parts = source.split(/\s+/).filter(Boolean)
      const letters = parts.slice(0, 2).map(part => part.charAt(0))
      return letters.join('') || '—'
    },
    // Readonly panel link, same construction ChatbotConversations.vue uses: the
    // panel proxies /admin/v1/ per operation slug, so the tenant slug has to
    // travel or the panel queries the wrong instance.
    chatbotPanelUrl () {
      const slug = this.$settings.get({ path: 'intake_operation_slug' })
      let url = `https://chatbot.test.panel.hasama.online/?applicant_id=${this.applicantId}&readonly=true`
      if (slug) url += `&operation=${slug}`
      return url
    },
    sortedMessages () {
      return this.messages
        .slice()
        .sort((a, b) => this.stampOf(a) - this.stampOf(b))
    },
    statsLine () {
      const list = this.sortedMessages
      if (!list.length) return ''
      const first = this.$moment(this.stampOf(list[0]))
      const last = this.$moment(this.stampOf(list[list.length - 1]))
      if (!first.isValid() || !last.isValid()) {
        return `${list.length} הודעות`
      }
      return `${list.length} הודעות · מ-${first.format('DD/MM/YY')} עד ${last.format('DD/MM/YY')}`
    },
    // One flat list of separators + bubbles, so the template stays a single
    // v-for and the "when did this change" decisions all live in one place.
    timeline () {
      const items = []
      let lastDay = null
      let lastConversationId = null

      this.sortedMessages.forEach((message, index) => {
        const day = this.$moment(this.stampOf(message))
        const dayKey = day.isValid() ? day.format('YYYY-MM-DD') : 'unknown'
        if (dayKey !== lastDay) {
          items.push({ kind: 'day', label: this.dayLabel(day) })
          lastDay = dayKey
        }

        const conversationId = message.conversation_id || null
        // No boundary pill before the very first message — there is nothing for
        // it to separate it from.
        if (index > 0 && conversationId && conversationId !== lastConversationId) {
          items.push({ kind: 'boundary', label: this.boundaryLabel(message) })
        }
        lastConversationId = conversationId

        items.push({ kind: 'message', message: message })
      })

      return items
    }
  },
  created () {
    this.load()
  },
  methods: {
    stampOf (message) {
      const raw = (message && (message.received_at || message.sent_at)) || null
      const value = raw ? new Date(raw).getTime() : NaN
      return isNaN(value) ? 0 : value
    },
    timeOf (message) {
      const stamp = this.stampOf(message)
      if (!stamp) return ''
      return this.$moment(stamp).format('HH:mm')
    },
    dayLabel (day) {
      if (!day.isValid()) return 'תאריך לא ידוע'
      const today = this.$moment().startOf('day')
      const start = day.clone().startOf('day')
      const diff = start.diff(today, 'days')
      if (diff === 0) return 'היום'
      if (diff === -1) return 'אתמול'
      return day.format('DD/MM/YYYY')
    },
    boundaryLabel (message) {
      const raw = message.conversation_started_at || message.received_at || message.sent_at
      const at = this.$moment(raw)
      if (!at.isValid()) return 'שיחה חדשה'
      return `שיחה חדשה · ${at.format('DD/MM/YY HH:mm')}`
    },
    mediaLabel (message) {
      const type = message && message.media_type
      if (!type || type === 'text') return ''
      return `[מדיה: ${type}]`
    },
    isFailed (message) {
      return message && message.direction === 'out' && message.status === 'failed'
    },
    load () {
      this.loading = true
      this.error = ''
      this.resolveConversationId()
        .then(conversationId => {
          if (!conversationId) {
            this.messages = []
            return null
          }
          return this.$network_utils.getConvSvcMessages(conversationId).then(res => {
            this.messages = (res && Array.isArray(res.messages)) ? res.messages : []
          })
        })
        .catch(() => {
          this.messages = []
          this.error = 'שגיאה בטעינת השיחה'
          this.$utils.emit('show_toast', { text: 'שגיאה בטעינת השיחה', type: 'error' })
        })
        .finally(() => {
          this.loading = false
          this.scrollToBottom()
        })
    },
    // The row usually already carries the offer's conversation id. When it does
    // not (older offers, or a row whose offer never reached conv-svc), the
    // applicant's most recent conversation is looked up instead — conv-svc
    // orders by last_event_at DESC, and any one id returns the whole history.
    resolveConversationId () {
      if (this.conversationId) return Promise.resolve(this.conversationId)
      if (!this.applicantId) return Promise.resolve(null)

      const slug = this.$settings.get({ path: 'intake_operation_slug' })
      return this.$network_utils
        .getConvSvcConversations({
          rebase_applicant_id: this.applicantId,
          per_page: 1,
          operation: slug || undefined
        })
        .then(res => {
          const rows = (res && Array.isArray(res.data)) ? res.data : []
          return rows.length && rows[0].id ? rows[0].id : null
        })
    },
    scrollToBottom () {
      this.$nextTick(() => {
        const surface = this.$refs.surface
        if (surface) surface.scrollTop = surface.scrollHeight
      })
    }
  }
}
</script>

<style scoped>
.chat-modal {
  text-align: right;
  color: #1f2937;
  font-size: 13px;
}

/* ---- header ---- */
.chat-head {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding-bottom: 14px;
  border-bottom: 1px solid #eef1f5;
  margin-bottom: 14px;
}
.chat-avatar {
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #e8f1ff;
  color: #1e3a8a;
  font-size: 14px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}
.chat-ident {
  min-width: 0;
}
.chat-name {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
  line-height: 1.3;
}
.chat-sub {
  margin-top: 3px;
  font-size: 12px;
  color: #6b7280;
}
.chat-phone {
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
  letter-spacing: 0.2px;
}
.chat-sep {
  margin: 0 5px;
  color: #cbd5e1;
}
.chat-stats {
  margin-top: 3px;
  font-size: 11px;
  color: #9ca3af;
}

/* ---- chat surface ---- */
.chat-surface {
  background: #f2efe9;
  border: 1px solid #e7e2da;
  border-radius: 12px;
  padding: 14px 12px;
  max-height: 60vh;
  overflow-y: auto;
}
.chat-pill-row {
  display: flex;
  justify-content: center;
  margin: 10px 0;
}
.chat-pill {
  background: #ffffff;
  border: 1px solid #e5e0d7;
  border-radius: 999px;
  padding: 2px 12px;
  font-size: 11px;
  color: #6b7280;
}
.chat-pill-boundary {
  background: #eef2ff;
  border-color: #dbe1fb;
  color: #4338ca;
}

.bubble-row {
  display: flex;
  margin-bottom: 6px;
}
.bubble-row.is-in {
  justify-content: flex-start;
}
.bubble-row.is-out {
  justify-content: flex-end;
}
.bubble {
  max-width: 78%;
  padding: 8px 12px;
  border-radius: 14px;
  font-size: 13.5px;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
  box-shadow: 0 1px 1px rgba(15, 23, 42, 0.06);
}
/* The corner nearest the sender is squared off, so side alone reads as "who
   said this" even before colour registers. */
.bubble.is-in {
  background: #ffffff;
  border: 1px solid #ece7df;
  border-start-start-radius: 4px;
}
.bubble.is-out {
  background: #dcf3e6;
  border: 1px solid #cbe9d8;
  border-start-end-radius: 4px;
}
.bubble-media {
  font-size: 11px;
  color: #6b7280;
  margin-bottom: 3px;
}
.bubble-text {
  color: #111827;
}
.bubble-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 4px;
  font-size: 10.5px;
  color: #9ca3af;
}
.bubble-time {
  white-space: nowrap;
}
.bubble-chip {
  background: rgba(15, 23, 42, 0.05);
  border-radius: 999px;
  padding: 1px 7px;
  color: #6b7280;
}
.bubble-failed {
  background: #fee2e2;
  border-radius: 999px;
  padding: 1px 7px;
  color: #b91c1c;
}

/* ---- states ---- */
.chat-state {
  background: #f8fafc;
  border: 1px solid #eef1f5;
  border-radius: 12px;
  padding: 28px 16px;
  text-align: center;
}
.chat-state-error {
  background: #fef6f6;
  border-color: #f7dcdc;
}
.chat-state-text {
  margin: 0 0 10px;
  font-size: 13px;
  color: #6b7280;
}
.chat-state-error .chat-state-text {
  color: #b91c1c;
}
.chat-retry {
  border: 1px solid #e5e7eb;
  background: #fff;
  border-radius: 8px;
  padding: 5px 16px;
  font-size: 12px;
  font-family: inherit;
  color: #1f2937;
  cursor: pointer;
}
.chat-retry:hover {
  background: #f3f4f6;
}

.skeleton-row {
  display: flex;
  margin-bottom: 8px;
}
.skeleton-row.is-in {
  justify-content: flex-start;
}
.skeleton-row.is-out {
  justify-content: flex-end;
}
.skeleton-bubble {
  width: 55%;
  border-radius: 14px;
  padding: 10px 12px;
  background: #ffffff;
  border: 1px solid #ece7df;
}
.skeleton-line {
  height: 9px;
  border-radius: 5px;
  margin-bottom: 6px;
  background: linear-gradient(90deg, #eceff3 25%, #f6f8fa 50%, #eceff3 75%);
  background-size: 240px 100%;
  animation: chat-shimmer 1.2s infinite linear;
}
.skeleton-line.is-short {
  width: 55%;
  margin-bottom: 0;
}
@keyframes chat-shimmer {
  0% { background-position: 240px 0; }
  100% { background-position: -240px 0; }
}

/* ---- footer ---- */
.chat-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 12px;
}
.chat-panel-link {
  font-size: 12px;
  color: #1d4ed8;
  text-decoration: none;
}
.chat-panel-link:hover {
  text-decoration: underline;
}
.chat-refresh {
  border: 1px solid #e5e7eb;
  background: #fff;
  border-radius: 999px;
  padding: 4px 14px;
  font-size: 12px;
  font-family: inherit;
  color: #4b5563;
  cursor: pointer;
}
.chat-refresh:hover:not(:disabled) {
  background: #f3f4f6;
}
.chat-refresh:disabled {
  opacity: 0.6;
  cursor: default;
}
</style>
